import { useCallback, useState, useMemo } from 'preact/hooks';
import { usePaginatedList } from '../../hooks/use-paginated-list';
import { getUsers } from '../../api/users';
import { Card } from '../../components/card';
import { Table, type Column } from '../../components/table';
import { Pagination } from '../../components/pagination';
import { Input } from '../../components/input';
import { Spinner } from '../../components/spinner';
import { EmptyState } from '../../components/empty-state';
import { Message } from '../../components/message';
import { SegmentPills } from '../../components/segment-pills';
import type { User } from '../../api/types';

interface UserTableProps {
  onSelect: (id: string) => void;
}

const LIMIT = 20;

const columns: Column<User>[] = [
  {
    header: 'User ID',
    key: 'id',
    render: (r) => r.id,
  },
  { header: 'Created', key: 'created_at', sortable: true, render: (r) => new Date(r.created_at).toLocaleDateString() },
  { header: 'Scans', key: 'scan_count', sortable: true },
  { header: 'Frameworks', key: 'framework_count', sortable: true },
];

function isRecent(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  return diff < 7 * 24 * 60 * 60 * 1000; // 7 days
}

export function UserTable({ onSelect }: UserTableProps) {
  const [activeSegment, setActiveSegment] = useState<string | null>(null);

  const fetcher = useCallback(async (page: number, search: string) => {
    const res = await getUsers({ page, limit: LIMIT, search: search || undefined });
    return { items: res.users, total: res.total };
  }, []);

  const list = usePaginatedList<User>(fetcher);

  // Compute segment counts from current page data
  const segments = useMemo(() => {
    const power = list.items.filter((u) => u.scan_count > 50).length;
    const recent = list.items.filter((u) => isRecent(u.created_at)).length;
    const multi = list.items.filter((u) => u.framework_count > 1).length;
    return [
      { id: 'power', label: 'Power Users', count: power },
      { id: 'new', label: 'New', count: recent },
      { id: 'multi', label: 'Multi-Framework', count: multi },
    ];
  }, [list.items]);

  // Filter displayed items by segment
  const displayedItems = useMemo(() => {
    if (!activeSegment) return list.items;
    switch (activeSegment) {
      case 'power': return list.items.filter((u) => u.scan_count > 50);
      case 'new': return list.items.filter((u) => isRecent(u.created_at));
      case 'multi': return list.items.filter((u) => u.framework_count > 1);
      default: return list.items;
    }
  }, [list.items, activeSegment]);

  return (
    <Card>
      <div class="toolbar">
        <Input
          value={list.search}
          onChange={list.setSearch}
          placeholder="Search by user ID..."
          icon="search"
        />
      </div>

      {!list.loading && list.items.length > 0 && (
        <div style={{ marginBottom: 'var(--sp-3)' }}>
          <SegmentPills
            segments={segments}
            activeSegment={activeSegment}
            onSelect={setActiveSegment}
          />
        </div>
      )}

      {list.loading ? (
        <Spinner text="Loading users" centered />
      ) : list.error ? (
        <Message text={list.error} type="error" />
      ) : displayedItems.length === 0 ? (
        <EmptyState icon="search" message="No users found" />
      ) : (
        <>
          <Table
            columns={columns as unknown as Column<Record<string, unknown>>[]}
            rows={displayedItems as unknown as Record<string, unknown>[]}
            onRowClick={(row) => onSelect(row.id as string)}
          />
          {!activeSegment && (
            <Pagination
              page={list.page}
              total={list.total}
              limit={LIMIT}
              onPageChange={list.setPage}
            />
          )}
        </>
      )}
    </Card>
  );
}
